import { Copy } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { useT } from "@/i18n";
import { previewApi, type SqlPreviewRequest } from "@/lib/builder-api";
import type { ReportConfig, DashboardConfigV2 } from "@/types";

interface SqlPreviewProps {
  /** Optional label rendered above the SQL block (e.g. "Aggregation expression"). */
  label?: string;
  /** Raw SQL string to display in Template mode. Empty / null shows the placeholder. */
  sql: string | null | undefined;
  /** Opt-in editing for item queries, never the shared runtime view. */
  onChange?: (sql: string) => void;
  /**
   * Optional helper text rendered between the label and the SQL box
   * (e.g. "This fragment is interpolated into a SELECT with the
   * current filter WHERE clause").
   */
  hint?: ReactNode;
  /** Placeholder shown when ``sql`` is empty. */
  emptyText?: string;
  /**
   * Iter 27 Phase 3b — when provided, render a Template/Live toggle.
   *
   * On Live, the component calls ``POST /api/builder/preview/sql`` to
   * reconstruct the executable SELECT the engine would run with the
   * current draft + active filters. Unsupported element types
   * (chart/table/text in v1) return ``supported=false`` from the
   * backend and the UI surfaces the explanation rather than emitting
   * a partial SQL — per the global no-fallback rule.
   */
  livePreview?: {
    reportConfig: ReportConfig;
    dashboardConfig: DashboardConfigV2 | Record<string, unknown>;
    activeFilters: Record<string, string>;
    projectSlug: string | null;
    elementType: SqlPreviewRequest["element_type"];
    elementId: string;
  };
}

type Mode = "template" | "live";

/**
 * Iter 27 — read-only SQL display block for the Dashboard inspector.
 *
 * Default mode renders ``sql`` (the template fragment stored in
 * ``dashboard_config``) as a monospace block with a copy button.
 * When the ``livePreview`` prop is set, a Template/Live radio toggle
 * appears: Live fetches the full SELECT the engine would execute
 * with the current active filters substituted in.
 *
 * Read-only by design: editing SQL happens via the report-builder
 * skill (Claude) or the AI tab's Examples editor, not by typing into
 * the inspector.
 */
export function SqlPreview({
  label,
  sql,
  hint,
  emptyText,
  livePreview,
  onChange,
}: SqlPreviewProps): JSX.Element {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pendingSql, setPendingSql] = useState(sql ?? "");
  useEffect(() => { setPendingSql(sql ?? ""); setEditing(false); }, [sql]);
  const [mode, setMode] = useState<Mode>("template");
  const [liveSql, setLiveSql] = useState<string | null>(null);
  const [liveNote, setLiveNote] = useState<string | null>(null);
  const [liveSupported, setLiveSupported] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset live state whenever the live-preview args change (e.g. user
  // selects a different element on the canvas).
  useEffect(() => {
    setLiveSql(null);
    setLiveNote(null);
    setLiveSupported(true);
    setError(null);
    setMode("template");
  }, [
    livePreview?.elementType,
    livePreview?.elementId,
    livePreview?.projectSlug,
  ]);

  // Fetch the live SQL on demand (only when the user switches to
  // Live and we don't have it cached yet for this element).
  useEffect(() => {
    if (mode !== "live" || !livePreview) return;
    if (liveSql !== null || liveNote !== null || error !== null) return;
    let cancelled = false;
    setLoading(true);
    void previewApi
      .sql({
        report_config: livePreview.reportConfig,
        dashboard_config: livePreview.dashboardConfig,
        active_filters: livePreview.activeFilters,
        project_slug: livePreview.projectSlug,
        element_type: livePreview.elementType,
        element_id: livePreview.elementId,
      })
      .then((res) => {
        if (cancelled) return;
        setLiveSql(res.live_sql);
        setLiveNote(res.note);
        setLiveSupported(res.supported);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof Error
            ? err.message
            : t("inspector.sql.live.error");
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, livePreview, liveSql, liveNote, error, t]);

  const displayText =
    mode === "live" ? (liveSql ?? "").trim() : (sql ?? "").trim();
  const isEmpty = displayText.length === 0;

  const handleCopy = async (): Promise<void> => {
    if (isEmpty) return;
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard unavailable (insecure context, etc.) — fail silently
      // rather than throw. The user can still select+copy manually.
    }
  };

  return (
    <div className="space-y-1">
      {onChange && <div className="space-y-2">
        <button type="button" className="text-xs underline"
          onClick={() => { setPendingSql(sql ?? ""); setEditing(!editing); }}>
          {t('inspector.sql.edit')}
        </button>
        {editing && <>
          <textarea aria-label={label ?? t('inspector.sql.edit')} value={pendingSql}
            onChange={(e) => setPendingSql(e.target.value)} rows={8}
            className="w-full rounded border p-2 font-mono text-xs" />
          <p className="text-xs text-neutral-500">{t('inspector.sql.editHint')}</p>
          <button type="button" disabled={!pendingSql.trim()} className="text-xs underline"
            onClick={() => { onChange(pendingSql); setEditing(false); }}>
            {t('inspector.sql.apply')}
          </button>
        </>}
      </div>}
      {(label || livePreview || !isEmpty) && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {label && (
              <span className="text-xs font-semibold text-neutral-500">
                {label}
              </span>
            )}
            {livePreview && (
              <span
                className="inline-flex rounded border border-neutral-200 bg-white p-0.5 text-xs"
                role="radiogroup"
                aria-label={t("inspector.sql.mode")}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "template"}
                  onClick={() => setMode("template")}
                  className={
                    mode === "template"
                      ? "rounded bg-neutral-900 px-1.5 py-0.5 font-semibold text-white"
                      : "rounded px-1.5 py-0.5 text-neutral-600 hover:bg-neutral-100"
                  }
                >
                  {t("inspector.sql.template")}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "live"}
                  onClick={() => setMode("live")}
                  className={
                    mode === "live"
                      ? "rounded bg-neutral-900 px-1.5 py-0.5 font-semibold text-white"
                      : "rounded px-1.5 py-0.5 text-neutral-600 hover:bg-neutral-100"
                  }
                >
                  {t("inspector.sql.live")}
                </button>
              </span>
            )}
          </div>
          {!isEmpty && (
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              title={t("common.copy")}
            >
              <Copy size={10} />
              {copied ? t("common.copied") : t("common.copy")}
            </button>
          )}
        </div>
      )}
      {hint && mode === "template" && (
        <p className="text-xs text-neutral-500 leading-snug">{hint}</p>
      )}
      {mode === "live" && livePreview && !liveSupported && liveNote && (
        <p className="rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-xs text-warning-dark leading-snug">
          {liveNote}
        </p>
      )}
      {mode === "live" && error && (
        <p className="rounded border border-error/30 bg-error-soft px-2 py-1.5 text-xs text-error-dark leading-snug">
          {error}
        </p>
      )}
      {mode === "live" && loading && !error && (
        <p className="rounded border border-dashed border-neutral-200 bg-neutral-50 px-2 py-1.5 text-xs italic text-neutral-500">
          {t("inspector.sql.live.loading")}
        </p>
      )}
      {!(mode === "live" && (loading || error)) && (
        isEmpty ? (
          <p className="rounded border border-dashed border-neutral-200 bg-neutral-50 px-2 py-1.5 text-xs italic text-neutral-500">
            {emptyText ?? t("inspector.sql.empty") ?? "—"}
          </p>
        ) : (
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-neutral-200 bg-neutral-900 px-2 py-1.5 font-mono text-xs leading-relaxed text-neutral-100">
            {displayText}
          </pre>
        )
      )}
    </div>
  );
}
